import { GoToChannel } from '@/_elements/Links';
import { BootstrapToolTip } from '@/_elements/ToolTip';
import { externalCalendarEventApi } from '@/_services/api/calendarIntegration/externalCalendarEvents';
import DynamicChannelSelectFilter from '@/components/Admin/DynamicChannelSelectFilter';
import TeamSelectFilter from '@/components/Admin/TeamSelectFilter';
import { filterTeams } from '@/components/Home/components/UpcomingEvents/CreateEvent';
import React, { Fragment, useMemo, useState } from 'react';
import { Button, Form, Modal } from 'react-bootstrap';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';

const BulkCopyExternalEventsToActivityLibraryModal = ({
    selectedEventIds = [],
    setSelectedEventIds,
    onBulkCopy,
    currUser,
    teams,
    teamOptions,
}) => {
    const [showModal, setShowModal] = useState(false);
    const [bulkCopyLoading, setBulkCopyLoading] = useState(false);
    const [selectedTeamOptions, setSelectedTeamOptions] = useState([]);
    const [selectedRoomOption, setSelectedRoomOption] = useState(null);
    const teamIds = useMemo(() => {
        return (teamOptions || []).map((item) => item?.value || '').filter(Boolean);
    }, [teamOptions]);

    const userEventCreateTeams = useMemo(() => {
        return filterTeams({
            isAdmin: currUser?.role === 'admin',
            teamList: currUser?.teamList || [],
            roomList: currUser?.roomList || [],
        });
    }, [currUser]);
    const handleShow = async () => {
        setShowModal(true);
    };

    const handleClose = () => {
        setShowModal(false);
    };

    const handleBulkCopy = async () => {
        if (!selectedEventIds.length) {
            return;
        }
        if (!selectedRoomOption?.value) {
            toast.error('Please select a channel');
            return;
        }
        const selectedEvents = selectedEventIds;
        if (!selectedEvents.length) {
            toast.error('Please select at least one event');
            return;
        }
        try {
            setBulkCopyLoading(true);
            await externalCalendarEventApi.bulkCopyToOptonome({
                chatroom: selectedRoomOption.value,
                externalCalendarEvents: selectedEvents,
            });
            toast.success('Selected events copied successfully');
            setSelectedEventIds([]);
            onBulkCopy();
            handleClose();
        } catch (error) {
            toast.error(error?.message || 'Failed to copy selected external events');
        } finally {
            setBulkCopyLoading(false);
        }
    };

    return (
        <Fragment>
            <BootstrapToolTip text={'Select 1 or more events to copy'}>
                <Button
                    variant="primary"
                    onClick={handleShow}
                    disabled={bulkCopyLoading || selectedEventIds.length === 0}
                >
                    {`Copy To Activity Library(${selectedEventIds.length})`}
                </Button>
            </BootstrapToolTip>

            <Modal centered show={showModal} onHide={handleClose}>
                <div style={{ border: '4px solid #ccc' }}>
                    <Modal.Header closeButton>
                        <Modal.Title>
                            <strong>Bulk Copy External Events to Channel/Team Activity Library</strong>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <Form.Row>
                            <div className="w-100">
                                <Form.Label>Team:</Form.Label>
                                <TeamSelectFilter
                                    teamIds={
                                        currUser?.role === 'admin'
                                            ? teams.map((t) => t._id)
                                            : userEventCreateTeams?.teamIds || []
                                    }
                                    customTeams={
                                        currUser?.role === 'admin' ? null : userEventCreateTeams?.teamList || []
                                    }
                                    selectedTeamOption={selectedTeamOptions}
                                    setSelectedTeamOption={setSelectedTeamOptions}
                                />
                            </div>
                        </Form.Row>

                        <Form.Label className="mt-3">Channel:</Form.Label>

                        <div className="d-flex" style={{ width: '100%', gap: '5px' }}>
                            <div style={{ width: '100%' }}>
                                <DynamicChannelSelectFilter
                                    selectedOption={selectedRoomOption}
                                    onRoomSelect={setSelectedRoomOption}
                                    assignedChannel={selectedRoomOption ? selectedRoomOption?.value : null}
                                    placeholder="Search channels"
                                    permissionRule="create:task"
                                    teamIds={selectedTeamOptions?.value ? [selectedTeamOptions?.value] : teamIds}
                                />
                            </div>
                            <div>
                                {selectedRoomOption && (
                                    <GoToChannel channel={{ ...selectedRoomOption, label: '', title: '' }} />
                                )}
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose}>
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            onClick={handleBulkCopy}
                            disabled={bulkCopyLoading || !selectedRoomOption?.value}
                        >
                            {bulkCopyLoading ? 'Copying...' : `Copy ${selectedEventIds.length} Events`}
                        </Button>
                    </Modal.Footer>
                </div>
            </Modal>
        </Fragment>
    );
};

const mapStateToProps = (state) => ({
    currUser: state.user.user,
    teams: state.teams.teams,
});
export default connect(mapStateToProps)(BulkCopyExternalEventsToActivityLibraryModal);
